import { usesAbsoluteDifference } from './perf-result.js';
import type { PerfResult, PerfRow, PerfSlowSpan, PerfVerdict } from './perf-result.js';

export type PerfRenderInput = {
  id: string;
  verdict: PerfVerdict;
  baseSha: string;
  headSha: string;
  result: PerfResult;
};

export function renderPerfResult(input: PerfRenderInput): string {
  const heading =
    input.verdict === 'slower' ? 'SLOWER' : input.verdict === 'faster' ? 'faster' : 'no change';
  const visibleRows = input.result.rows.filter(
    (row) =>
      row.gated ||
      (row.flavor === 'otel' &&
        row.id.endsWith(':p95') &&
        (row.base.values.length < input.result.rounds ||
          row.head.values.length < input.result.rounds))
  );
  const omittedUngatedRows = input.result.rows.filter((row) => !visibleRows.includes(row));
  const lines = [
    `## Perf: ${heading} (head ${shortSha(input.headSha)} vs base ${shortSha(input.baseSha)}, ${input.result.rounds} rounds)`,
    '',
    '| Metric | Base | Head | Delta | 95% CI | Verdict |',
    '|---|---|---|---|---|---|',
    ...visibleRows.map((row) => renderRow(row, input.result.rounds)),
  ];
  const ungated = omittedUngatedRows.length;
  if (ungated > 0)
    lines.push(
      '',
      `Not gated: ${ungated} more rows (${ungatedLabels(omittedUngatedRows)}). \`--json\` for all rows.`
    );
  const tools = [
    input.result.tools.lighthouse && `lighthouse ${input.result.tools.lighthouse}`,
    input.result.tools.k6 && `k6 ${input.result.tools.k6}`,
    input.result.tools.otelcol && `otelcol-contrib ${input.result.tools.otelcol}`,
  ].filter((tool): tool is string => tool !== undefined);
  if (ungated === 0) lines.push('');
  lines.push(`Tools: ${tools.length > 0 ? tools.join(', ') : 'none'}. Run: ${input.id}.`);
  if (input.verdict === 'slower' && input.result.otel) {
    lines.push(
      '',
      '### Slowest server spans (head)',
      '',
      ...renderSpans(input.result.otel.topSlowSpans.head.slice(0, 5))
    );
    const baseByName = new Map(
      input.result.otel.topSlowSpans.base.map((span) => [span.name, span])
    );
    const slower = input.result.otel.topSlowSpans.head.filter((span) => {
      const base = baseByName.get(span.name);
      return !base || span.p95Ms > base.p95Ms;
    });
    if (slower.length > 0)
      lines.push('', '### New or slower than base', '', ...renderSpans(slower));
  }
  return lines.join('\n');
}

function ungatedLabels(rows: PerfRow[]): string {
  const labels = rows.map((row) => {
    if (row.id.endsWith(':fcp')) return 'FCP';
    if (row.id.endsWith(':cls')) return 'CLS';
    if (row.id.endsWith(':speed-index')) return 'speed index';
    if (row.id.endsWith(':bytes')) return 'bytes';
    if (row.id.endsWith(':p50')) return 'p50s';
    return row.label;
  });
  const order = ['FCP', 'CLS', 'speed index', 'bytes', 'p50s'];
  const standard = order.filter((label) => labels.includes(label));
  const extra = [...new Set(labels.filter((label) => !order.includes(label)))];
  return [...standard, ...extra].join(', ');
}

function renderRow(row: PerfRow, rounds: number): string {
  const sparseEndpoint =
    row.flavor === 'otel' &&
    row.id.endsWith(':p95') &&
    (row.base.values.length < rounds || row.head.values.length < rounds);
  const verdict = row.verdict === 'slower' ? 'SLOWER' : row.verdict.replace('_', ' ');
  const label = sparseEndpoint ? `${row.label} (not gated)` : row.label;
  return `| ${escapeCell(label)} | ${formatArmValue(row.base, row.unit)} | ${formatArmValue(row.head, row.unit)} | ${formatDelta(row)} | ${formatInterval(row)} | ${verdict} |`;
}

function formatArmValue(arm: PerfRow['base'], unit: PerfRow['unit']): string {
  return arm.values.length === 0 ? '-' : formatValue(arm.median, unit);
}

export function formatValue(value: number, unit: PerfRow['unit']): string {
  switch (unit) {
    case 'ms':
      return `${round(value)} ms`;
    case 'rps':
      return `${round(value)}`;
    case 'ratio':
      return `${(value * 100).toFixed(1)}%`;
    case 'bytes':
      return `${round(value)} bytes`;
    case 'count':
      return `${round(value)}`;
  }
}

export function formatDelta(row: PerfRow): string {
  const missingBase = row.base.values.length === 0;
  const missingHead = row.head.values.length === 0;
  if (missingBase) return 'new';
  if (missingHead) return 'removed';
  if (row.unit === 'ratio') return `${signed(row.deltaPct, 1)} pt`;
  const displayedDelta = row.unit === 'rps' ? -row.deltaPct : row.deltaPct;
  if (usesAbsoluteDifference(row)) {
    return `${signed(displayedDelta, 0)}${unitSuffix(row.unit)}`;
  }
  return `${signed(displayedDelta * 100, 1)}%`;
}

export function formatInterval(row: PerfRow): string {
  const missingBase = row.base.values.length === 0;
  const missingHead = row.head.values.length === 0;
  if (missingBase || missingHead) return '-';
  const absolute = usesAbsoluteDifference(row);
  const suffix = row.unit === 'ratio' ? ' pt' : absolute ? unitSuffix(row.unit) : '%';
  const scale = row.unit === 'ratio' || absolute ? 1 : 100;
  const precision = suffix === '%' || row.unit === 'ratio' ? 1 : 0;
  const lowerBound = row.unit === 'rps' ? -row.ci95[1] : row.ci95[0];
  const upperBound = row.unit === 'rps' ? -row.ci95[0] : row.ci95[1];
  const lower = signed(lowerBound * scale, precision);
  const upper = signed(upperBound * scale, precision);
  return suffix === '%' ? `${lower}% .. ${upper}%` : `${lower} .. ${upper}${suffix}`;
}

export function unitSuffix(unit: PerfRow['unit']): string {
  switch (unit) {
    case 'ms':
      return ' ms';
    case 'rps':
      return ' rps';
    case 'ratio':
      return ' pt';
    case 'bytes':
      return ' bytes';
    case 'count':
      return '';
  }
}

function renderSpans(spans: PerfSlowSpan[]): string[] {
  if (spans.length === 0) return ['No server spans.'];
  return [
    '| Name | Count | Worst round p95 | Total duration |',
    '|---|---:|---:|---:|',
    ...spans.map(
      (span) =>
        `| ${escapeCell(span.name)} | ${span.count} | ${round(span.p95Ms)} ms | ${round(span.totalMs)} ms |`
    ),
  ];
}

export function shortSha(sha: string): string {
  return sha.slice(0, 7);
}

export function signed(value: number, precision = 1): string {
  const rounded = Number(value.toFixed(precision));
  return `${rounded >= 0 ? '+' : ''}${rounded.toFixed(precision)}`;
}

function round(value: number): number {
  return Number(value.toFixed(1));
}

function escapeCell(value: string): string {
  return value.replaceAll('|', '\\|').replaceAll('\n', ' ');
}
